import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/stores'
import { usePermissions } from '@/hooks/use-permissions'
import { useMyVendorIds } from '@/hooks/use-vendor-portal'
import { useArchiveBoards } from '@/hooks'
import { OpsyriLogo, OpsyriIcon } from '@/components/ui/opsyri-logo'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  mainNavItems, cmmsNavItems, archivesCmmsItem, planningNavItems,
  managementNavItems, salesNavItems, toolsNavItems, settingsNavItems,
  vendorPortalNavItems, clientPortalNavItems, type NavItem,
} from './nav-items'

// Nav lists live in ./nav-items so the desktop sidebar and the mobile drawer
// render from the same source (they used to drift).
export function Sidebar() {
  const location = useLocation()
  const { sidebarCollapsed, setSidebarCollapsed } = useAppStore()
  const { isVendor, isClient, canAccess, canAccessInspections, isCornerstoneOrg } = usePermissions()

  // Cross-org vendor link — shows a "Vendor Portal" shortcut for users who wear both hats
  const { data: myVendors } = useMyVendorIds()
  const hasVendorLink = (myVendors?.length ?? 0) > 0

  // Single consolidated "Archived Work Orders" entry for 2021-2025 history.
  // The page itself has a year filter for users who want to drill into a
  // specific year. Only render the entry when the registry actually has
  // archive boards configured for this org.
  const { data: archiveBoards } = useArchiveBoards()
  const hasArchives = (archiveBoards?.length ?? 0) > 0

  // Collapsible section state — all open by default
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggleSection = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const filterByPermission = (items: NavItem[]) =>
    items.filter((item) => !item.page || canAccess(item.page))

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')

    return (
      <Link
        to={item.href}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground',
          sidebarCollapsed && 'justify-center px-2'
        )}
        title={sidebarCollapsed ? item.title : undefined}
      >
        <item.icon className="h-5 w-5 shrink-0" />
        {!sidebarCollapsed && <span>{item.title}</span>}
      </Link>
    )
  }

  const NavSection = ({ label, sectionKey, items }: { label: string; sectionKey: string; items: NavItem[] }) => {
    const filtered = filterByPermission(items)
    if (filtered.length === 0) return null
    const isCollapsed = collapsed[sectionKey] ?? false

    return (
      <>
        {!sidebarCollapsed ? (
          <button
            onClick={() => toggleSection(sectionKey)}
            className="mt-6 mb-1 px-3 w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>{label}</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                isCollapsed && '-rotate-90'
              )}
            />
          </button>
        ) : (
          <Separator className="my-4" />
        )}

        {(!isCollapsed || sidebarCollapsed) && (
          <nav className="space-y-1">
            {filtered.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        )}
      </>
    )
  }

  return (
    <aside
      className={cn(
        'hidden border-r bg-background lg:flex lg:flex-col',
        sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'flex h-16 items-center border-b px-4',
        sidebarCollapsed ? 'justify-center' : 'gap-2'
      )}>
        {sidebarCollapsed ? (
          <OpsyriIcon size={28} />
        ) : (
          <OpsyriLogo className="h-7" />
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        {isVendor ? (
          // Vendor portal nav
          <nav className="space-y-1">
            {vendorPortalNavItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        ) : isClient ? (
          // Client portal nav — hide inspections unless invited
          <nav className="space-y-1">
            {clientPortalNavItems
              .filter((item) => item.href !== '/portal/client/inspections' || canAccessInspections)
              .filter((item) => item.href !== '/portal/client/tours' || isCornerstoneOrg)
              .map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
          </nav>
        ) : (
          // Internal user nav
          <>
            <nav className="space-y-1">
              {filterByPermission(mainNavItems).map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </nav>

            {/* CMMS section — with the single Archives entry appended after
                Work Orders when the registry has archive boards. One sidebar
                entry instead of five keeps the rail compact and matches the
                user's mental model (one history list with a year filter). */}
            <NavSection
              label="CMMS"
              sectionKey="cmms"
              items={
                hasArchives
                  ? [cmmsNavItems[0], archivesCmmsItem, ...cmmsNavItems.slice(1)]
                  : cmmsNavItems
              }
            />
            <NavSection label="Planning" sectionKey="planning" items={planningNavItems} />
            <NavSection label="Management" sectionKey="management" items={managementNavItems} />
            <NavSection label="Sales / Prospecting" sectionKey="sales" items={salesNavItems} />
            <NavSection label="Tools" sectionKey="tools" items={toolsNavItems} />

            {hasVendorLink && (
              <>
                <Separator className="my-4" />
                <Link
                  to="/portal/vendor"
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    'border border-primary/20 bg-primary/10 text-primary hover:bg-primary/15',
                    location.pathname.startsWith('/portal/vendor') && 'ring-1 ring-primary/40',
                    sidebarCollapsed && 'justify-center px-2'
                  )}
                  title={sidebarCollapsed ? 'Vendor Portal' : undefined}
                >
                  <ExternalLink className="h-5 w-5 shrink-0" />
                  {!sidebarCollapsed && <span>Vendor Portal</span>}
                </Link>
              </>
            )}
          </>
        )}
      </ScrollArea>

      {/* Settings & Team & Collapse */}
      <div className="border-t p-3">
        {settingsNavItems
          .filter((item) => !item.page || canAccess(item.page))
          .map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                // Exact match everywhere except Help, whose page has sub-routes.
                // A startsWith on '/settings' would light up Settings while the
                // user is on /settings/team.
                (item.href === '/help'
                  ? location.pathname.startsWith('/help')
                  : location.pathname === item.href) && 'bg-accent text-accent-foreground',
                sidebarCollapsed && 'justify-center px-2'
              )}
              title={sidebarCollapsed ? item.title : undefined}
            >
              <item.icon className="h-5 w-5" />
              {!sidebarCollapsed && <span>{item.title}</span>}
            </Link>
          ))}

        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'mt-2 w-full',
            sidebarCollapsed ? 'justify-center px-2' : 'justify-start'
          )}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 mr-2" />
              <span>Collapse</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  )
}
